/**
 * Editor text-edit module — barrel export.
 *
 * The factory `createViewportTextEditor(viewer, hooks)` is the standard
 * entry point. It dynamic-imports `./ViewportTextEditor` at call time, so
 * consumers that never call the factory tree-shake the controller and its
 * Babylon dependencies (`BoundingBoxGizmo`, `UtilityLayerRenderer`,
 * `MeshBuilder`, `StandardMaterial`) out of their bundles entirely.
 *
 * IMPORTANT: this barrel intentionally does NOT re-export
 * `ViewportTextEditor` as a runtime value — a static export would pull the
 * Babylon gizmo classes into every bundle that touches
 * `trace-viewer/editor`, defeating the lazy-load. Only the `type` is
 * re-exported so consumers can annotate the factory's return value.
 *
 * Lifecycle mirrors `createGizmoController`: call the factory after the
 * viewer is ready (the controller's double-tap observer must register
 * after TraceViewer's own camera-reset handler), store the returned
 * controller, and `dispose()` it before the scene is torn down (viewer
 * `src` change, view unmount) or before creating one for a new scene.
 */

import type { TraceViewerElement } from '../../core/TraceViewer';
import type { ViewportTextEditor, ViewportTextEditorHooks } from './ViewportTextEditor';

export type { ViewportTextEditor, ViewportTextEditorHooks } from './ViewportTextEditor';

/**
 * Create and attach an inline viewport text editor for the given viewer.
 * See the module doc above for lifecycle rules, and the controller class
 * doc for host responsibilities (gizmo suppression via
 * `hooks.onSessionActive`).
 */
export async function createViewportTextEditor(
    viewer: TraceViewerElement,
    hooks: ViewportTextEditorHooks = {},
): Promise<ViewportTextEditor> {
    const { ViewportTextEditor: Controller } = await import('./ViewportTextEditor');
    const editor = new Controller(viewer, hooks);
    editor.attach();
    return editor;
}
